import { Badge } from '@shared/ui/Badge';
import { IconScales, IconAlertTriangle } from '@shared/ui/icons';
import { formatMoney } from '../financierFormat';
import {
  ThermometerCard,
  ThermometerHeader,
  ThermometerTitle,
  ThermometerStats,
  StatItem,
  StatLabel,
  StatVal,
  LiquidityErrorText,
  RetryMiniButton,
  ProgressTrack,
  Segment,
  DeficitBanner,
} from './styled';

interface CashThermometerProps {
  isLiquidityConfirmed: boolean;
  coveragePct: number | null;
  isDashboardLoading: boolean;
  isDashboardError: boolean;
  availableCash: number | null;
  freeCash: number;
  remainingToDebit: number;
  totalPaidInView: number;
  isDeficit: boolean;
  projectedCashAfterRegistry: number | null;
  onRefetchDashboard: () => void;
}

export function CashThermometer({
  isLiquidityConfirmed,
  coveragePct,
  isDashboardLoading,
  isDashboardError,
  availableCash,
  freeCash,
  remainingToDebit,
  totalPaidInView,
  isDeficit,
  projectedCashAfterRegistry,
  onRefetchDashboard,
}: CashThermometerProps) {
  return (
    <ThermometerCard>
      <ThermometerHeader>
        <ThermometerTitle>
          <IconScales size={18} />
          <span>Балансировщик ликвидности расчётных счетов</span>
          {isLiquidityConfirmed && coveragePct !== null ? (
            <Badge $tone={coveragePct >= 100 ? 'success' : coveragePct >= 70 ? 'warning' : 'danger'}>
              Покрытие: {Math.round(coveragePct)}%
            </Badge>
          ) : isDashboardError ? (
            <Badge $tone="danger">Ошибка ликвидности</Badge>
          ) : (
            <Badge $tone="neutral">Загрузка…</Badge>
          )}
        </ThermometerTitle>

        <ThermometerStats>
          <StatItem>
            <StatLabel>Доступный кэш:</StatLabel>
            <StatVal $tone={availableCash !== null ? '#15803d' : undefined}>
              {isDashboardLoading ? (
                'Загрузка данных ликвидности…'
              ) : isDashboardError ? (
                <LiquidityErrorText>
                  Ошибка загрузки
                  <RetryMiniButton
                    $size="sm"
                    $variant="ghost"
                    onClick={onRefetchDashboard}
                  >
                    Повторить
                  </RetryMiniButton>
                </LiquidityErrorText>
              ) : (
                formatMoney(availableCash ?? 0)
              )}
            </StatVal>
          </StatItem>
          <StatItem>
            <StatLabel>Остаток к списанию (не оплачено):</StatLabel>
            <StatVal>{formatMoney(remainingToDebit)}</StatVal>
          </StatItem>
          <StatItem>
            <StatLabel>Оплачено по выписке:</StatLabel>
            <StatVal $tone="#0369a1">{formatMoney(totalPaidInView)}</StatVal>
          </StatItem>
          <StatItem>
            <StatLabel>Прогноз после реестра:</StatLabel>
            <StatVal $tone={isDeficit ? '#b91c1c' : '#15803d'}>
              {isLiquidityConfirmed && projectedCashAfterRegistry !== null
                ? formatMoney(projectedCashAfterRegistry)
                : '—'}
            </StatVal>
          </StatItem>
        </ThermometerStats>
      </ThermometerHeader>

      <ProgressTrack>
        <Segment
          $width={freeCash > 0 ? (totalPaidInView / freeCash) * 100 : 0}
          $color="#10b981"
          title={`Оплачено по выписке: ${formatMoney(totalPaidInView)}`}
        />
        <Segment
          $width={freeCash > 0 ? (remainingToDebit / freeCash) * 100 : 0}
          $color="#0284c7"
          title={`Остаток к списанию: ${formatMoney(remainingToDebit)}`}
        />
        <Segment
          $width={
            freeCash > 0 && projectedCashAfterRegistry !== null && projectedCashAfterRegistry > 0
              ? (projectedCashAfterRegistry / freeCash) * 100
              : 0
          }
          $color="#334155"
          title={`Свободный нераспределённый остаток: ${formatMoney(projectedCashAfterRegistry ?? 0)}`}
        />
      </ProgressTrack>

      {isDeficit && projectedCashAfterRegistry !== null && (
        <DeficitBanner>
          <IconAlertTriangle size={18} />
          <span>
            <b>Кассовый разрыв {formatMoney(Math.abs(projectedCashAfterRegistry))}!</b> Сумма утверждённых платежей превышает доступный остаток на расчётных счетах. Рекомендуется уменьшить суммы (30%/50%) по низкоприоритетным заявкам — остаток автоматически перенесётся в следующий реестр.
          </span>
        </DeficitBanner>
      )}
    </ThermometerCard>
  );
};
